import type { StateCreator } from 'zustand';
import { CondoStore } from './shared';
import { apiService } from '../../services/api';

// These action creators are intentionally NOT members of the `CondoStore`
// interface — every caller reads them through a `useCondoStore() as any` (or a
// `(s: any) => …` selector), matching the pre-refactor shape where they lived in
// the store's object literal but were never typed. The slice return type is
// `object` (not `Partial<CondoStore>`) so TypeScript does not flag these as
// excess properties, preserving the original byte-identical, untyped behaviour.
export const createExtrasSlice: StateCreator<CondoStore, [], [], object> = (set) => ({
  // ── Unit Management (S10.1) ──────────────────────────────────────────────

  fetchUnitsForCondo: async (condoId: string) => {
    set({ isLoading: true });
    try {
      const units = await apiService.fetchUnitsForCondo(condoId);
      set({ units, error: null });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch units' });
    } finally {
      set({ isLoading: false });
    }
  },

  createUnitRecord: async (data: any) => {
    const created = await apiService.createUnit(data);
    set((state: any) => ({ units: [...(state.units || []), created] }));
    return created;
  },

  updateUnitRecord: async (id: string, data: any) => {
    const updated = await apiService.updateUnit(id, data);
    set((state: any) => ({ units: (state.units || []).map((u: any) => u.id === id ? updated : u) }));
    return updated;
  },

  deleteUnitRecord: async (id: string) => {
    await apiService.deleteUnit(id);
    set((state: any) => ({ units: (state.units || []).filter((u: any) => u.id !== id) }));
  },

  // ── Board E-Voting (S10.2) ────────────────────────────────────────────────

  castResolutionVote: async (resolutionId: string, vote: string) => {
    const updated = await apiService.castVote(resolutionId, vote as any);
    set((state: any) => ({
      boardResolutions: (state.boardResolutions || []).map((r: any) => r.id === resolutionId ? updated : r),
    }));
    return updated;
  },

  // ── Maintenance → WO Pipeline (S10.3) ─────────────────────────────────────

  createWorkOrderFromRequest: async (requestId: string) => {
    const wo = await apiService.createWorkOrderFromRequest(requestId);
    set((state: any) => ({
      workOrders: [...(state.workOrders || []), wo],
    }));
    return wo;
  },

  // ── Financial Reports (S10.4) ─────────────────────────────────────────────

  fetchArAgingReport: async (condoId: string) => {
    return await apiService.getArAgingReport(condoId);
  },

  fetchDelinquencyReport: async (condoId: string) => {
    return await apiService.getDelinquencyReport(condoId);
  },

  fetchIncomeStatement: async (condoId: string, from?: string, to?: string) => {
    return await apiService.getIncomeStatement(condoId, from, to);
  },

  fetchBudgetVariance: async (condoId: string) => {
    return await apiService.getBudgetVariance(condoId);
  },

  fetchBalanceSheet: async (condoId: string) => {
    return await apiService.getBalanceSheet(condoId);
  },
});
